import { useEffect } from "react";

import { matchPath, useLocation } from "react-router-dom";

import { postTrackingData } from "@/api/endpoints/track";
import { EVENT, PAGE_EVENT } from "@/tracking_events";
import { parseQuery } from "@/utils";
import * as Sentry from "@sentry/browser";

/** This is my take on how we should score the routes. Bascially any route with : in it
 * has a positional parameter which will be matched against anything.
    example:
    /a/:something
    will match:
    /a/dishwasher
    ... If dishwasher is something completely different that needs to come higher up in the
    ordered list to be tracked first. Therefore I am reducing the score of any routes with : in them.
 */
const calculateRouteComplexity = (route: string) => {
  return route.split("/").length - route.split(":").length;
};

const byRouteComplexity = (a: string, b: string) => {
  return calculateRouteComplexity(a) > calculateRouteComplexity(b) ? -1 : 1;
};

/**
 * Order matters here. The first route that matches will be used.
 * Sorting the routes will allow the matcher to match the most complex routes first,
 * never missing a route.
 */
const sortedRoutes = Object.entries(PAGE_EVENT).sort((a, b) =>
  byRouteComplexity(a[1], b[1])
);

/**
 * TrackPageView
 * - Listens to route changes
 * - On each route change, it identifies the matching route from ROUTES.
 * - Sends tracking data including the page name and relevant parameters
 * - Any errors encountered during this process are logged to Sentry.
 */

const TrackPageView = () => {
  const { hash, pathname, search } = useLocation();

  useEffect(() => {
    const params = parseQuery(search) as object;

    try {
      for (const [pageName, value] of sortedRoutes) {
        const m = matchPath(pathname, {
          path: value,
          exact: true,
        });
        if (m) {
          postTrackingData(EVENT.PAGE_VIEW, {
            ...params,
            ...m?.params,
            "Page Name": pageName,
            // Add the hash back on
            url: `${pathname}${hash ? hash : ""}`,
          });

          // stop searching
          return;
        }
      }
    } catch (err) {
      // If something fails here we don't reaaaallly care. Let's log it to Sentry
      // to keep track of if a problem occurs, but we shouldn't error out because of this.
      Sentry.captureException(err);
    }
  }, [hash, pathname, search]);

  return null;
};
export default TrackPageView;
